<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-semibold text-gray-800">Patient Report</h2>
    </div>
    <div v-if="loading" class="py-10 text-center text-sm text-gray-400">Loading…</div>
    <DataTable
      v-else
      :columns="columns"
      :rows="rowData"
      row-key="bene_mbi_id"
      max-height="700px"
      saved-view-key="ext-patient-report"
      :has-saved="hasSavedView"
      :saved-just-saved="savedViewJustSaved"
      v-model:sort-model="sortModel"
      export-title="Patient Report"
      export-filename="cclf_patient_report"
      empty-text="No patient data."
      @save-view="saveView"
      @reset-view="resetView"
    />
  </div>
</template>

<script>
import { sensitiveCell } from '@/utils/demoSpoof'
import { DataTable } from '@/components/External/table'
import savedView from '@/mixins/savedView'

export default {
  name: 'CCLFReport_PatientReport',
  components: { DataTable },
  mixins: [savedView],
  savedView: {
    key: 'ext-patient-report',
    fields: ['sortModel'],
    scopeProp: 'entityId',
  },
  props: {
    contractId: { type: String, default: null },
    performanceYear: { type: Number, default: null },
    dosthru: { type: String, default: null },
    entityId: { type: Number, default: null },
    careSetting: { type: String, default: '' },
  },
  data() {
    return {
      rowData: [],
      loading: false,
      sortModel: { key: null, dir: 'asc' },
      columns: [
        // Demographics
        { field: 'bene_last_name', header: 'Last Name', type: 'text', sticky: true, width: 130, filterable: true, cellClass: () => sensitiveCell('person-last') },
        { field: 'bene_first_name', header: 'First Name', type: 'text', width: 120, filterable: true, cellClass: () => sensitiveCell('person-first') },
        { field: 'bene_mbi_id', header: 'MBI', type: 'text', width: 130, filterable: true, cellClass: () => sensitiveCell('identifier') },
        { field: 'patient_status', header: 'Status', type: 'text', width: 90, filterable: true },
        { field: 'bene_death_dt', header: 'Date of Death', type: 'date', width: 115, cellClass: () => sensitiveCell('date') },
        { field: 'facility_name', header: 'Patient Facility', type: 'text', width: 200, filterable: true, cellClass: () => 'sensitive' },
        { field: 'bene_pcp', header: 'PCP', type: 'text', width: 200, filterable: true, cellClass: () => 'sensitive' },
        // Visits
        { field: 'first_visit_dt', header: 'First Visit', type: 'date', width: 110 },
        { field: 'last_awv_dt', header: 'Last AWV', type: 'date', width: 105 },
        { field: 'prior_year_awv_dt', header: 'Prior Yr AWV', type: 'date', width: 115 },
        { field: 'last_ov_dt', header: 'Last Office Visit', type: 'date', width: 120 },
        { field: 'num_ip_visits', header: 'IP Admits', type: 'number', width: 90 },
        { field: 'num_ed_visits', header: 'ED Visits', type: 'number', width: 90 },
        { field: 'num_readmissions', header: 'Readmits', type: 'number', width: 90 },
        { field: 'snf_days', header: 'SNF Days', type: 'number', width: 90 },
        // Risk
        { field: 'hcc_score', header: 'HCC Score', type: 'number', width: 100, format: v => v == null ? '—' : Number(v).toFixed(2) },
        { field: 'open_hcc_gaps', header: 'Open HCC Gaps', type: 'number', width: 120 },
        // Cost
        { field: 'total_cost', header: 'Total Cost', type: 'currency', width: 120 },
        { field: 'ip_cost', header: 'IP Cost', type: 'currency', width: 110 },
        { field: 'snf_cost', header: 'SNF Cost', type: 'currency', width: 110 },
        { field: 'op_cost', header: 'OP Cost', type: 'currency', width: 110 },
        { field: 'part_b_cost', header: 'Part B', type: 'currency', width: 110 },
        { field: 'dme_cost', header: 'DME', type: 'currency', width: 100 },
        { field: 'hh_cost', header: 'Home Health', type: 'currency', width: 110 },
        { field: 'hospice_cost', header: 'Hospice', type: 'currency', width: 100 },
      ],
    }
  },
  watch: {
    careSetting() { this.loadData() },
    contractId() { this.loadData() },
    performanceYear() { this.loadData() },
    dosthru() { this.loadData() },
    entityId() { this.loadData() },
  },
  mounted() {
    if (this.contractId && this.performanceYear) this.loadData()
  },
  methods: {
    async loadData() {
      if (!this.contractId || !this.performanceYear) return
      this.loading = true
      try {
        const response = await this.$axios.post('/api/portfolio_management/cclf-report/patient-report/', {
          contract_id: this.contractId,
          performance_year: this.performanceYear,
          dosthru: this.dosthru,
          entity_id: this.entityId,
          care_setting: this.careSetting || undefined,
        })
        this.rowData = response.data
      } catch (error) {
        console.error('Failed to load patient report:', error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
